// src/components/AnalyticsOrgHealthTable.js

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faUsers } from '@fortawesome/free-solid-svg-icons';
import { LineChart, Line } from 'recharts';
import { CHART_COLORS, formatRecency, daysSince } from './analyticsChartUtils';
import AnalyticsOrgDetailPanel from './AnalyticsOrgDetailPanel';

const FEATURES = [
  { key: 'attendance', label: 'Att' },
  { key: 'communications', label: 'Comms' },
  { key: 'newsletter', label: 'News' },
  { key: 'events', label: 'Events' },
  { key: 'groups', label: 'Groups' },
];

function FeatureBadge({ label, usage }) {
  const recency = formatRecency(usage.lastUsedAt);
  const since = daysSince(usage.lastUsedAt);

  let tier = 'never';
  let text = 'never';
  if (recency !== null) {
    tier = since <= 30 ? 'recent' : 'stale';
    text = recency;
  } else if (usage.total > 0) {
    // Groups can exist with no recorded activity
    tier = 'stale';
    text = `${usage.total}`;
  }

  return (
    <span className={`analytics-feature-badge badge-${tier}`} title={`${label}: last used ${text}`}>
      {label} · {text}
    </span>
  );
}

function Sparkline({ data }) {
  const hasActivity = data && data.some((d) => d.count > 0);
  if (!hasActivity) {
    return <span className="analytics-sparkline-empty">no recent activity</span>;
  }
  // Fixed size on purpose: ResponsiveContainer collapses to zero height in table cells
  return (
    <LineChart width={120} height={32} data={data} margin={{ top: 4, right: 2, left: 2, bottom: 2 }}>
      <Line
        type="monotone"
        dataKey="count"
        stroke={CHART_COLORS.series}
        strokeWidth={1.5}
        dot={false}
        isAnimationActive={false}
      />
    </LineChart>
  );
}

function AnalyticsOrgHealthTable({ orgs, days, expandedOrgId, onToggle }) {
  if (!orgs || orgs.length === 0) {
    return (
      <div className="analytics-empty">
        <div className="analytics-empty-icon">
          <FontAwesomeIcon icon={faUsers} />
        </div>
        <h4>No Organizations</h4>
        <p>No organization data was returned.</p>
      </div>
    );
  }

  const sorted = [...orgs].sort((a, b) => b.users.active7d - a.users.active7d);

  return (
    <div className="analytics-table-wrap">
      <table className="analytics-table">
        <thead>
          <tr>
            <th>Organization</th>
            <th className="center">Users</th>
            <th className="center">Directory</th>
            <th>Trend ({days}d)</th>
            <th>Feature Usage (last {days}d)</th>
            <th className="center">Details</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((org) => {
            const expanded = expandedOrgId === org.orgId;
            return (
              <React.Fragment key={org.orgId}>
                <tr className="analytics-org-row" onClick={() => onToggle(org.orgId)}>
                  <td>
                    <div className="analytics-org-name">{org.orgName}</div>
                    <div className="analytics-org-id">{org.orgId}</div>
                  </td>
                  <td className="center">
                    <div className="analytics-user-stat">
                      <span className="analytics-user-total">{org.users.total}</span>
                      <span className="analytics-user-active">
                        {org.users[`active${days}d`] ?? org.users.active7d} active {days}d
                      </span>
                    </div>
                  </td>
                  <td className="center">
                    <div className="analytics-user-stat">
                      <span className="analytics-user-active">{org.memberCount} members</span>
                      <span className="analytics-user-active">{org.familyCount} families</span>
                    </div>
                  </td>
                  <td>
                    <Sparkline data={org.sparkline} />
                  </td>
                  <td>
                    <div className="analytics-feature-badges">
                      {FEATURES.map((f) => (
                        <FeatureBadge key={f.key} label={f.label} usage={org.featureAdoption[f.key]} />
                      ))}
                    </div>
                  </td>
                  <td className="center">
                    <button
                      className="analytics-expand-btn"
                      aria-expanded={expanded}
                      aria-label={`${expanded ? 'Hide' : 'Show'} details for ${org.orgName}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggle(org.orgId);
                      }}
                    >
                      <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                    </button>
                  </td>
                </tr>
                {expanded && (
                  <tr className="analytics-daily-row">
                    <td colSpan={6}>
                      <AnalyticsOrgDetailPanel orgId={org.orgId} orgName={org.orgName} days={days} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AnalyticsOrgHealthTable;
